import React from "react";

interface EditableProps {
  text: string;
  onEdit?: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit = () => {},
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  return (
    <div
      contentEditable
      onBlur={(e) => onEdit(e.currentTarget.innerText)}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {text || placeholder}
    </div>
  );
};

const TitleSlide = {
  id: "title-slide",
  name: "Title Slide",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Exploring the World of Science",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "A Journey Through Discovery and Innovation",
    },
    {
      id: "content",
      label: "Description",
      type: "text",
      defaultValue:
        "Join us as we uncover the mysteries of the natural world and explore groundbreaking scientific concepts.",
    },
  ],
  render: (content: any) => {
    const {
      title,
      subtitle,
      content: description,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: string) => (value: string) => {
      onEdit({ ...content, [field]: value });
    };

    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white relative overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
              backgroundSize: "40px 40px",
            }}
          ></div>
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute bg-white/20 rounded-full"
              style={{
                width: Math.random() * 6 + 2 + "px",
                height: Math.random() * 6 + 2 + "px",
                left: Math.random() * 100 + "%",
                top: Math.random() * 100 + "%",
                animation: `float ${Math.random() * 10 + 5}s linear infinite`,
                animationDelay: `-${Math.random() * 10}s`,
              }}
            />
          ))}
        </div>

        {/* Main Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-8 text-center">
          {/* Title */}
          <h1 className="text-7xl font-black mb-8 leading-tight tracking-tight animate-title">
            <EditableDiv
              text={title}
              onEdit={handleEdit("title")}
              className="bg-clip-text text-transparent bg-gradient-to-r from-blue-200 via-blue-100 to-white"
            />
          </h1>

          {/* Subtitle */}
          <EditableDiv
            text={subtitle}
            onEdit={handleEdit("subtitle")}
            className="text-3xl font-light mb-8 text-blue-200 animate-subtitle"
          />

          {/* Animated Separator */}
          <div className="flex items-center justify-center mb-8 animate-separator">
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
            <div className="mx-4">
              <svg
                className="w-6 h-6 text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 12h14M12 5l7 7-7 7"
                />
              </svg>
            </div>
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>
          </div>

          {/* Description */}
          <EditableDiv
            text={description}
            onEdit={handleEdit("content")}
            className="text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto animate-description"
          />
        </div>

        {/* Bottom Gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
      </div>
    );
  },
};

// Add required styles to the global CSS
const style = document.createElement("style");
style.textContent = `
  @keyframes float {
    0% {
      transform: translateY(0) translateX(0);
    }
    25% {
      transform: translateY(-20px) translateX(10px);
    }
    50% {
      transform: translateY(0) translateX(20px);
    }
    75% {
      transform: translateY(20px) translateX(10px);
    }
    100% {
      transform: translateY(0) translateX(0);
    }
  }

  .animate-title {
    animation: fade-scale-up 0.8s cubic-bezier(0.11, 0, 0.5, 0) forwards;
  }

  .animate-subtitle {
    animation: fade-scale-up 0.8s cubic-bezier(0.11, 0, 0.5, 0) forwards;
    animation-delay: 0.2s;
  }

  .animate-separator {
    animation: fade-scale-up 0.8s cubic-bezier(0.11, 0, 0.5, 0) forwards;
    animation-delay: 0.4s;
  }

  .animate-description {
    animation: fade-scale-up 0.8s cubic-bezier(0.11, 0, 0.5, 0) forwards;
    animation-delay: 0.6s;
  }

  @keyframes fade-scale-up {
    0% {
      opacity: 0;
      transform: scale(0.8) translateY(20px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;
document.head.appendChild(style);

export default TitleSlide;
